import { useCallback, useEffect, useState, type RefObject } from 'react'

/** Marge in px: subpixel-afronding telt niet als "nog niet aan het eind". */
const SLACK = 2

/**
 * Staat een horizontaal scrollende rij aan het begin of het eind? Voor pijlknoppen die uitgaan
 * als er niets meer te zien is. Hooguit één meting per beeld, en opnieuw na een formaatwijziging.
 */
export function useScrollEnds(ref: RefObject<HTMLElement>) {
  const [ends, setEnds] = useState({ atStart: true, atEnd: false })

  const measure = useCallback(() => {
    const el = ref.current
    if (!el) return
    const atStart = el.scrollLeft <= SLACK
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - SLACK
    setEnds((prev) => (prev.atStart === atStart && prev.atEnd === atEnd ? prev : { atStart, atEnd }))
  }, [ref])

  useEffect(() => {
    const el = ref.current
    if (!el) return
    let frame = 0
    const onScroll = () => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        measure()
      })
    }
    measure()
    el.addEventListener('scroll', onScroll, { passive: true })
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    ro?.observe(el)
    return () => {
      el.removeEventListener('scroll', onScroll)
      ro?.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [ref, measure])

  return ends
}
